import { Link } from "react-router-dom";
import { useAuthUser } from "@/hooks/useAuthUser";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Users, Shield, Mail, Clock, Tag } from "lucide-react";
import { AuthActionButtons } from "@/components/auth/AuthActionButtons";
import { useProviderAuth } from "@/hooks/useProviderAuth";

export default function Home() {
  const { profile, isSignedIn } = useAuthUser();
  const currentUser = profile; // For compatibility
  const auth = useProviderAuth();

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-gradient-to-b from-[#669933] to-[#00B0F0] text-white">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 flex items-center justify-center gap-2">
              <span className="text-white">CalOne</span>
              <span className="text-white"> Events</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-white font-medium">
              Simple Event Registration That Just Works
            </p>
            <p className="text-lg mb-8 text-white/90">
              Create and share event registration pages in minutes. No account needed for attendees.
            </p>
            {currentUser ? (
              <Link to="/dashboard">
                <Button size="lg" variant="secondary" className="font-semibold">
                  Go to Dashboard <ArrowRight className="ml-2" />
                </Button>
              </Link>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <AuthActionButtons
                  returnTo="/dashboard"
                  size="lg"
                  signInVariant="secondary"
                  signInClassName="font-semibold w-full sm:w-auto"
                  signUpClassName="font-semibold bg-white text-secondary hover:bg-white/90 w-full sm:w-auto"
                  showIcons
                  signUpLabel="Create Account"
                />
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4">
        <section className="py-16">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-6">
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Easy Setup</h3>
              <p className="text-muted-foreground">
                Create your event page in minutes with our intuitive interface.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-secondary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Simple for Attendees</h3>
              <p className="text-muted-foreground">
                No login required. Share the link and let attendees register.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Powerful Features</h3>
              <p className="text-muted-foreground">
                Waitlists, custom fields, and automatic notifications included.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 border-t">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Everything You Need</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="flex gap-4">
                <Mail className="h-6 w-6 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">Automatic Notifications</h3>
                  <p className="text-muted-foreground">Send confirmation emails and reminders to attendees automatically.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <Clock className="h-6 w-6 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">Smart Waitlist</h3>
                  <p className="text-muted-foreground">Automatically manage capacity and waitlist registrations.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <Tag className="h-6 w-6 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">Custom Fields</h3>
                  <p className="text-muted-foreground">Collect exactly the information you need from attendees.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 border-t text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join event organizers who trust CalOne Events.
            </p>
            {!currentUser && (
              <div className="flex justify-center">
                <Button
                  size="lg"
                  className="font-semibold bg-[#00B0F0] hover:bg-[#0099d6] text-white px-8"
                  onClick={() => auth.signUp("/dashboard")}
                >
                  Create Your First Event <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="bg-muted/20 border-t">
        <div className="container mx-auto px-4 py-12">
          <div className="flex justify-center">
            <div className="flex flex-col items-center text-center max-w-sm">
              <Link to="/" className="text-2xl font-bold text-gray-800 flex items-center gap-2 justify-center">
                <img
                  src="https://v1.calone.com/static/graphics/CalOne_31_Logo.png"
                  alt="CalOne Logo"
                  className="h-8 w-8"
                />
                <span>
                  <span className="text-[#669933]">CalOne</span>
                  <span>
                    {" "}
                    <span className="text-[#4285F4]">E</span>
                    <span className="text-[#DB4437]">v</span>
                    <span className="text-[#F4B400]">e</span>
                    <span className="text-[#0F9D58]">n</span>
                    <span className="text-[#4285F4]">t</span>
                    <span className="text-[#9C27B0]">s</span>
                  </span>
                </span>
              </Link>
              <p className="mt-4 text-muted-foreground">
                Simple event registration that just works.
              </p>
            </div>
          </div>

          <div className="border-t mt-12 pt-8 text-center text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} <span className="text-[#669933]">CalOne</span>
            <span>
              {" "}
              <span className="text-[#4285F4]">E</span>
              <span className="text-[#DB4437]">v</span>
              <span className="text-[#F4B400]">e</span>
              <span className="text-[#0F9D58]">n</span>
              <span className="text-[#4285F4]">t</span>
              <span className="text-[#9C27B0]">s</span>
            </span>
            . All rights reserved.
            
          </div>
        </div>
      </footer>
    </div>
  );
}
